import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Grid, Modal, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import SeatLayout from 'components/booking/SeatLayout';
import tableService from 'services/tableService';
import intraService from 'services/intraService';
import { BlockedLocation, LanBooking, LanRegistration } from 'types/intra';
import { TableGroup } from 'types/common';
import { bookedLocations, bookingParticipantLabel, resolveSeatAssignIntent, toErrorMessage } from './intraUtils';
import SeatSwapConfirmDialog from './SeatSwapConfirmDialog';
import 'assets/Table.scss';

interface SeatAssignModalProps {
    opened: boolean;
    booking: LanBooking | null;
    bookings: LanBooking[];
    registrations: LanRegistration[];
    accessToken: string | null;
    isOperating: (key: string) => boolean;
    onClose: () => void;
    onAssign: (bookingId: string, location: string | null) => Promise<void>;
    onSwap: (bookingId: string, location: string) => Promise<void>;
}

const SeatAssignModal: React.FC<SeatAssignModalProps> = ({
    opened,
    booking,
    bookings,
    registrations,
    accessToken,
    isOperating,
    onClose,
    onAssign,
    onSwap
}) => {
    const [tableGroups, setTableGroups] = useState<TableGroup[]>([]);
    const [blocked, setBlocked] = useState<BlockedLocation[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [pendingSwap, setPendingSwap] = useState<{ location: string; occupant: LanBooking } | null>(null);

    const loadMapData = useCallback(async () => {
        setLoading(true);
        try {
            const [groups, blockedLocations] = await Promise.all([
                tableService.getAllTableGroups(),
                intraService.fetchBlockedLocations(accessToken ?? undefined)
            ]);
            setTableGroups(groups);
            setBlocked(blockedLocations);
            setError(null);
        } catch (err) {
            setError(toErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [accessToken]);

    useEffect(() => {
        if (opened) {
            setPendingSwap(null);
            loadMapData();
        }
    }, [opened, loadMapData]);

    const occupied = useMemo(() => bookedLocations(bookings), [bookings]);
    const blockedIds = useMemo(() => blocked.map(item => item.location), [blocked]);

    const handleSeatClick = async (location: string) => {
        if (!booking || isOperating(`booking-location-${booking.id}`)) {
            return;
        }

        const intent = resolveSeatAssignIntent(booking, location, bookings);
        if (intent.type === 'swap') {
            setPendingSwap({ location: intent.location, occupant: intent.occupant });
            return;
        }

        try {
            await onAssign(booking.id, intent.type === 'clear' ? null : intent.location);
            setError(null);
            onClose();
        } catch (err) {
            setError(toErrorMessage(err));
        }
    };

    const handleConfirmSwap = async () => {
        if (!booking || !pendingSwap) {
            return;
        }
        try {
            await onSwap(booking.id, pendingSwap.location);
            setPendingSwap(null);
            setError(null);
            onClose();
        } catch (err) {
            setError(toErrorMessage(err));
        }
    };

    return (
        <>
            <Modal
                opened={opened}
                onClose={onClose}
                title={booking ? `Sijoita: ${bookingParticipantLabel(booking, registrations)}` : 'Sijoita paikka'}
                size="auto"
                centered
            >
                <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                        Valitse vapaa tai varattu paikka, myös blokattu. Varattu paikka avaa vahvistuksen
                        paikanvaihdosta. Klikkaa nykyistä paikkaa poistaaksesi sijoituksen.
                    </Text>
                    {error && (
                        <Alert color="red" icon={<IconInfoCircle size={16} />}>
                            {error}
                        </Alert>
                    )}
                    <div className="table-clearfix">
                        <Grid>
                            <Grid.Col span={3}>
                                <div className="table-legend table-free"></div>
                                <span>Vapaa</span>
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <div className="table-legend table-disabled"></div>
                                <span>Blokkattu</span>
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <div className="table-legend table-booking"></div>
                                <span>Valittu</span>
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <div className="table-legend table-booked"></div>
                                <span>Varattu</span>
                            </Grid.Col>
                        </Grid>
                    </div>
                    {loading ? (
                        <Text>Ladataan pohjakarttaa...</Text>
                    ) : (
                        <SeatLayout
                            tableGroups={tableGroups}
                            booked={occupied}
                            booking={booking?.location ?? ''}
                            toggle={handleSeatClick}
                            blockedLocations={blockedIds}
                            allowSelectOccupied
                        />
                    )}
                </Stack>
            </Modal>
            <SeatSwapConfirmDialog
                opened={Boolean(pendingSwap)}
                source={booking}
                occupant={pendingSwap?.occupant ?? null}
                location={pendingSwap?.location ?? ''}
                registrations={registrations}
                confirming={Boolean(booking && isOperating(`booking-location-${booking.id}`))}
                onConfirm={handleConfirmSwap}
                onCancel={() => setPendingSwap(null)}
            />
        </>
    );
};

export default SeatAssignModal;
